import { Injectable, inject, signal } from '@angular/core';
import { finalize, take, timer } from 'rxjs';
import { UserContextService } from '../../../common/user-management/auth-management/authentication/services/user-context.service';
import { IWalletResponse } from '../dtos';
import { WalletsApiClientProvider } from '../clients';

/**
 * Session-wide wallet state shared by the header, the Home hero and the checkout pages.
 *
 * Ownership of the initial load: `BaseComponent` (the authenticated layout shell) calls `ensureWalletLoaded()` as
 * soon as the layout activates. The header and pages only read `wallet` / `isResolved`. The header still calls
 * `ensureWalletLoaded()` because three standalone pages embed it outside the layout; inside the layout that call is
 * always a no-op, so a page load issues a single request.
 */
@Injectable({
  providedIn: 'root'
})
export class WalletStateService {
  private walletsProvider = inject(WalletsApiClientProvider);
  private _wallet = signal<IWalletResponse | null>(null);
  private _isLoading = signal(false);
  private _isResolved = signal(false);
  private walletOwnerKey: string | undefined;
  private requestSeq = 0;

  readonly wallet = this._wallet.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  /**
   * True once the first request has settled (success or failure) and until `clear()`. It stays true during later
   * refreshes, so consumers can tell "still loading the first time" (show a skeleton) from "have a value" or
   * "failed" without the skeleton coming back on every refresh.
   */
  readonly isResolved = this._isResolved.asReadonly();

  /** Idempotent initial load: does nothing while a request is in flight or after the first one has settled. */
  ensureWalletLoaded(): void {
    this.discardWalletOfOtherUser();
    if (this._isLoading() || this._isResolved()) {
      return;
    }
    this.fetchWallet();
  }

  /** Explicit fresh fetch. Concurrent calls coalesce into the request already in flight. */
  refreshWallet(): void {
    this.discardWalletOfOtherUser();
    if (this._isLoading()) {
      return;
    }
    this.fetchWallet();
  }

  /** After wallet debits, first getMyWallet can return stale balance; follow-up refresh syncs the header pill. */
  refreshWalletAfterMutation(): void {
    this.refreshWallet();
    timer(500)
      .pipe(take(1))
      .subscribe(() => this.refreshWallet());
  }

  setWallet(wallet: IWalletResponse | null): void {
    this._wallet.set(wallet);
    if (wallet) {
      this.walletOwnerKey = WalletStateService.currentOwnerKey();
      this._isResolved.set(true);
    }
  }

  clear(): void {
    this.requestSeq++; // a response still in flight belongs to the session that just ended
    this._wallet.set(null);
    this._isLoading.set(false);
    this._isResolved.set(false);
    this.walletOwnerKey = undefined;
  }

  private fetchWallet(): void {
    const seq = ++this.requestSeq;
    this.walletOwnerKey = WalletStateService.currentOwnerKey();
    this._isLoading.set(true);
    this.walletsProvider
      .getClient()
      .getMyWallet()
      .pipe(
        finalize(() => {
          if (seq === this.requestSeq) {
            this._isLoading.set(false);
          }
        })
      )
      .subscribe({
        next: (wallet) => {
          if (seq === this.requestSeq) {
            this._wallet.set(wallet);
            this._isResolved.set(true);
          }
        },
        error: () => {
          if (seq === this.requestSeq) {
            this._isResolved.set(true);
          }
        }
      });
  }

  /**
   * The service outlives a session, and not every way a session ends calls `clear()` (forced logout on token
   * refresh failure, an expired token found by `ensureAuthenticated`, a logout from a standalone page). Since
   * `ensureWalletLoaded()` no longer refetches on every layout mount, a different user signing in within the same
   * tab must never inherit the previous user's balance.
   */
  private discardWalletOfOtherUser(): void {
    if (this.walletOwnerKey !== WalletStateService.currentOwnerKey()) {
      this.clear();
    }
  }

  /**
   * Most stable identity available for the signed-in user: the user id, then the account id (both from the
   * user context), and only as a last resort the refresh token, which is per login session and fails safe by
   * causing one extra fetch rather than sharing a balance.
   */
  private static currentOwnerKey(): string | undefined {
    const userContext = UserContextService.getUserContext();
    return userContext?.id || userContext?.accountId || UserContextService.getToken()?.refreshToken || undefined;
  }
}
